import { Component, ElementRef, Input, Output, EventEmitter, OnInit, AfterViewInit, ViewChild, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { AbstractControl, FormControl, FormGroupDirective, FormsModule, NgForm, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { ErrorStateMatcher, MatOptionModule } from '@angular/material/core';
import { CommonModule } from '@angular/common';
import { Country } from '../../models/interfaces/country.interface';
import { Subject, takeUntil } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';

/** Shows error state based on the validity of the actual countryControl, not the search input. */
class CountryErrorStateMatcher implements ErrorStateMatcher {
  constructor(private readonly countryControl: AbstractControl) {}

  isErrorState(_control: FormControl | null, _form: FormGroupDirective | NgForm | null): boolean {
    return !!(this.countryControl.invalid && (this.countryControl.dirty || this.countryControl.touched));
  }
}

@Component({
  selector: 'enfaf-country-selector',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatAutocompleteModule,
    MatOptionModule
  ],
  templateUrl: './enfaf-country-selector.component.html',
  styleUrls: ['./enfaf-country-selector.component.scss']
})
export class EnfafCountrySelectorComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() set countries(countries: Country[]) {
    this.allCountries = countries || [];
    if (this.countryControl && !this.countryControl.value) {
      this._setSpainDefault();
    }
  }
  @Input() countryControl: any;
  @Output() countryChange = new EventEmitter<string>();
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;
  @ViewChild('countryInput') inputRef!: ElementRef<HTMLInputElement>;

  /** Dedicated control for the visible text input (search). Does NOT store Country objects. */
  searchControl = new FormControl<string>('');
  errorStateMatcher!: CountryErrorStateMatcher;

  private allCountries: Country[] = [];
  private _destroyed$ = new Subject<void>();

  constructor(private cdr: ChangeDetectorRef) {}

  /** Computed on every change detection cycle — always in sync with what the user has typed. */
  get filteredCountriesList(): Country[] {
    const text = this.searchControl.value || '';
    return text ? this._filter(text) : this.allCountries.slice();
  }

  ngOnInit(): void {
    this.errorStateMatcher = new CountryErrorStateMatcher(this.countryControl);

    // If countries were already loaded before this component initialized, default to Spain
    if (!this.countryControl.value && this.allCountries.length > 0) {
      this._setSpainDefault();
    }

    // Initialize the search input text with the currently selected country's name (if any)
    const currentValue = this.countryControl.value;
    if (currentValue && typeof currentValue === 'object') {
      this.searchControl.setValue(currentValue.name, { emitEvent: false });
    }

    // Keep search input text in sync when countryControl is updated externally
    this.countryControl.valueChanges.pipe(takeUntil(this._destroyed$)).subscribe((value: Country | null) => {
      if (value && typeof value === 'object') {
        this.searchControl.setValue(value.name, { emitEvent: false });
        this.countryChange.emit(value.countryId);
      } else if (!value) {
        this.searchControl.setValue('', { emitEvent: false });
      }
    });
  }

  ngAfterViewInit(): void {
    // Run change detection after the view is initialized to avoid
    // ExpressionChangedAfterItHasBeenCheckedError
    this.cdr.detectChanges();
  }

  onPanelOpened(): void {
    // Keep existing text so the user sees the current selection and can refine from there
  }

  clearSelection(event: MouseEvent): void {
    event.stopPropagation();
    this.countryControl.setValue(null);
    this.countryControl.markAsTouched();
    this.searchControl.setValue('', { emitEvent: false });
    // Focus the input and open the panel so the user can immediately type to search
    setTimeout(() => {
      this.inputRef.nativeElement.focus();
      this.autocompleteTrigger.openPanel();
    });
  }

  onPanelClosed(): void {
    // Restore the display text to the currently selected country's name
    const current = this.countryControl.value;
    const displayName = current && typeof current === 'object' ? current.name : '';
    this.searchControl.setValue(displayName, { emitEvent: false });
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    const selectedCountry: Country = event.option.value;
    this.countryControl.setValue(selectedCountry);
  }

  getFlagFromCountryId(countryId: string): string | null {
    const country = this.allCountries.find((c) => c.countryId === countryId);
    return country?.flag || null;
  }

  /** Finds Spain by countryId or by name ('Spain' / 'España') and sets it as the default value. */
  private _setSpainDefault(): void {
    const spain = this.allCountries.find((c) => c.countryId === 'es' || c.name === 'Spain' || c.name === 'España');
    if (spain) {
      this.countryControl.setValue(spain);
    }
  }

  private _filter(name: string): Country[] {
    const filterValue = name.toLowerCase();
    return this.allCountries.filter((country) => country.name.toLowerCase().includes(filterValue));
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }
}
